import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';

const CATEGORY_LABELS = {
  engagement:        'Engagement',
  tax_return:        'Tax Return',
  irs_correspondence:'IRS',
  audit:             'Audit',
  year_end_summary:  'Year-End',
  custom:            'Custom',
};

export default function LetterTemplatePicker({ onSelect, selected }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get('/api/letter-templates/', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTemplates(r.data || []))
      .catch(() => setError('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (CATEGORY_LABELS[t.category] || '').toLowerCase().includes(search.toLowerCase())
  );

  const systemTemplates = filtered.filter((t) => t.is_system_template);
  const customTemplates = filtered.filter((t) => !t.is_system_template);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const TemplateCard = ({ tpl }) => (
    <Card
      variant="outlined"
      sx={{
        border: selected?.id === tpl.id ? 2 : 1,
        borderColor: selected?.id === tpl.id ? 'primary.main' : 'divider',
        transition: 'border-color 0.15s',
      }}
    >
      <CardActionArea onClick={() => onSelect(tpl)} sx={{ p: 1 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={700} sx={{ flex: 1, pr: 1 }}>
              {tpl.name}
            </Typography>
            <Chip
              label={CATEGORY_LABELS[tpl.category] || tpl.category}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          </Box>
          {tpl.subject_template && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {tpl.subject_template}
            </Typography>
          )}
          {tpl.is_system_template && (
            <Chip label="System" size="small" color="info" sx={{ mt: 0.5, fontSize: 10, height: 18 }} />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search templates…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      {systemTemplates.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
            STANDARD TEMPLATES
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {systemTemplates.map((t) => (
              <Grid item xs={12} sm={6} key={t.id}>
                <TemplateCard tpl={t} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {customTemplates.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
            YOUR FIRM'S TEMPLATES
          </Typography>
          <Grid container spacing={1}>
            {customTemplates.map((t) => (
              <Grid item xs={12} sm={6} key={t.id}>
                <TemplateCard tpl={t} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No templates found.
        </Typography>
      )}
    </Box>
  );
}
